import type { ReactNode } from 'react';

export type CpmBadgeTone =
    | 'success'
    | 'warning'
    | 'danger'
    | 'info'
    | 'open'
    | 'brand'
    | 'neutral';

interface CpmBadgeProps {
    tone?: CpmBadgeTone;
    /** 6px currentColor disc — design uses this on list-row Active / Inactive. */
    dot?: boolean;
    children: ReactNode;
    className?: string;
}

const TONES: Record<CpmBadgeTone, { bg: string; border: string; color: string }> = {
    success: {
        bg: 'var(--cpm-success-bg)',
        border: 'var(--cpm-success-border)',
        color: 'var(--cpm-success-text)',
    },
    warning: {
        bg: 'var(--cpm-warning-bg)',
        border: 'var(--cpm-warning-border)',
        color: 'var(--cpm-warning-text)',
    },
    danger: {
        bg: 'var(--cpm-shortfall-bg)',
        border: 'var(--cpm-shortfall-border)',
        color: 'var(--cpm-shortfall-text)',
    },
    info: {
        bg: 'var(--cpm-info-bg)',
        border: 'var(--cpm-info-border)',
        color: 'var(--cpm-info-text)',
    },
    open: {
        bg: 'var(--cpm-open-bg)',
        border: 'var(--cpm-open-border)',
        color: 'var(--cpm-open-text)',
    },
    brand: {
        bg: 'var(--cpm-purple-tint-13)',
        border: 'transparent',
        color: 'var(--cpm-brand-purple)',
    },
    neutral: {
        bg: 'var(--cpm-surface-tag)',
        border: 'transparent',
        color: 'var(--cpm-text-secondary)',
    },
};

/**
 * Matches the design system's `Badge` (docs2 `_ds_bundle.js`).
 * Active = success + dot; Inactive = neutral + dot; sentence-case label.
 */
export default function CpmBadge({
    tone = 'neutral',
    dot = false,
    children,
    className = '',
}: CpmBadgeProps) {
    const t = TONES[tone];
    return (
        <span
            className={['inline-flex items-center whitespace-nowrap', className].join(' ')}
            style={{
                gap: 6,
                padding: '3px 9px',
                borderRadius: 'var(--cpm-radius-full)',
                background: t.bg,
                border: `1px solid ${t.border}`,
                color: t.color,
                fontFamily: 'var(--cpm-font-sans)',
                fontSize: 11,
                fontWeight: 800,
                letterSpacing: '0.03em',
                width: 'max-content',
                maxWidth: 'max-content',
                justifySelf: 'start',
                alignSelf: 'center',
            }}
        >
            {dot ? (
                <span
                    style={{
                        width: 6,
                        height: 6,
                        flex: 'none',
                        borderRadius: 'var(--cpm-radius-full)',
                        background: 'currentColor',
                    }}
                />
            ) : null}
            {children}
        </span>
    );
}
